/**
 * Deduplication contract (F14, milestone M3): duplicate candidates, links,
 * study clusters and PRISMA deduplication counts. Re-exported from index.ts.
 *
 * Owner: deduplication builder (F14). Shapes follow
 * specs/features/F14-deduplication/design.md, Interfaces, with two changes:
 * the design's CandidateSuggestion, CandidateState and CandidateReason are
 * named DedupCandidate* here, because F18's design defines a different
 * CandidateState (living candidates) in the same package namespace; and a
 * candidate's records are DedupCandidateRecord (RecordListing plus the fields
 * the review screen compares, DEDUP-067), because RecordListing has no
 * abstract, volume or pages.
 */
import { pagePath } from './paths';
import type { RecordAuthor, RecordKind, RecordListing } from './search';

export const DEDUP_ROUTES = {
  /** GET ?state&suggestion&limit&cursor -> 200 DedupCandidateListResponse (E108; DEDUP-060). */
  dedupCandidates: (slug: string) => `${pagePath(slug)}/dedup/candidates`,
  /** PUT DuplicateOfRequest -> 200 RecordListing & RecordDedupDetail; null undoes a link (E32; DEDUP-046, DEDUP-063). */
  recordDuplicateOf: (slug: string, recordId: string) => `${pagePath(slug)}/records/${encodeURIComponent(recordId)}/duplicate-of`,
  /** POST DedupDecisionRequest -> 200 DedupCandidate; 409 conflict with details.current (E109; DEDUP-061, DEDUP-062). */
  dedupCandidateDecision: (slug: string, candidateId: string) =>
    `${pagePath(slug)}/dedup/candidates/${encodeURIComponent(candidateId)}/decision`,
  /** POST DedupReopenRequest -> 200 DedupCandidate in state open, its decision reversed (E110; DEDUP-069). */
  dedupCandidateReopen: (slug: string, candidateId: string) =>
    `${pagePath(slug)}/dedup/candidates/${encodeURIComponent(candidateId)}/reopen`,
  /** GET ?limit&cursor&registryId -> 200 StudyClusterListResponse (E111; DEDUP-059). */
  studyClusters: (slug: string) => `${pagePath(slug)}/study-clusters`,
  /** PUT MoveToClusterRequest -> 200 StudyCluster (E112; DEDUP-055). */
  recordStudyCluster: (slug: string, recordId: string) => `${pagePath(slug)}/records/${encodeURIComponent(recordId)}/study-cluster`,
  /** POST -> 202 {jobId}; 409 while a run is active (E113; DEDUP-086). */
  dedupRecompute: (slug: string) => `${pagePath(slug)}/dedup/recompute`,
} as const;

export type IdentifierScheme =
  | 'doi'
  | 'pmid'
  | 'pmcid'
  | 'openalex'
  | 'europepmc'
  | 'nct'
  | 'isrctn'
  | 'euct'
  | 'eudract'
  | 'actrn'
  | 'chictr'
  | 'ctri'
  | 'drks'
  | 'jrct'
  | 'umin'
  | 'irct'
  | 'pactr'
  | 'kct'
  | 'tctr'
  | 'slctr'
  | 'rbr'
  | 'ntr';

export interface RecordIdentifier {
  scheme: IdentifierScheme;
  value: string;
  origin: 'source_field' | 'text';
}

export type DedupMethod = 'doi' | 'pmid' | 'pmcid' | 'fuzzy' | 'human';
export type DedupCandidateSuggestion = 'duplicate' | 'same_study' | 'multi_trial';
export type DedupCandidateState = 'open' | 'duplicate' | 'same_study' | 'unrelated' | 'obsolete';
export type DedupCandidateReason =
  | 'identifier_conflict'
  | 'title_below_auto_threshold'
  | 'short_title'
  | 'first_author_differs'
  | 'first_author_missing'
  | 'year_differs'
  | 'year_missing'
  | 'volume_conflict'
  | 'page_conflict'
  | 'different_kind'
  | 'registry_conflict'
  | 'multi_registry_same_scheme'
  | 'human_link_differs'
  | 'registration_title_match';

/** A candidate's record with everything the review screen compares (DEDUP-060, DEDUP-067). */
export interface DedupCandidateRecord extends RecordListing {
  /** Every listed author, not only the first 10. */
  allAuthors: RecordAuthor[];
  abstract: string | null;
  volume: string | null;
  issue: string | null;
  pages: string | null;
  identifiers: RecordIdentifier[];
}

export interface DedupCandidate {
  /** 'dc_<uuid>'. */
  id: string;
  /** A has the smaller id (for multi_trial, A is the report of several trials). */
  recordA: DedupCandidateRecord;
  recordB: DedupCandidateRecord;
  /** Title similarity, 6 decimal places. */
  score: number | null;
  reasons: DedupCandidateReason[];
  suggestion: DedupCandidateSuggestion;
  /** DEDUP-040's choice, for 'duplicate'. */
  suggestedCanonicalId: string | null;
  state: DedupCandidateState;
  version: number;
  /** 'user:<id>'. */
  decidedBy: string | null;
  decidedAt: string | null;
  createdRunId: string | null;
  createdAt: string;
}

/** GET DEDUP_ROUTES.dedupCandidates (DEDUP-060). */
export interface DedupCandidateListResponse {
  candidates: DedupCandidate[];
  nextCursor: string | null;
  openCount: number;
}

/** POST dedup/candidates/{id}/decision (E109; DEDUP-061, DEDUP-062). */
export interface DedupDecisionRequest {
  decision: 'duplicate' | 'same_study' | 'unrelated';
  /** Required for 'duplicate'; must be A or B. */
  canonicalRecordId?: string;
  expectedVersion: number;
  /** The reviewer's note, up to 500 characters, kept with the decision in the audit log (DEDUP-066, DEDUP-068). */
  note?: string;
}

/** POST DEDUP_ROUTES.dedupCandidateReopen (E110; DEDUP-069). */
export interface DedupReopenRequest {
  expectedVersion: number;
}

/** POST DEDUP_ROUTES.dedupRecompute (E113; DEDUP-086). */
export interface DedupRecomputeResponse {
  jobId: string;
}

/** PUT DEDUP_ROUTES.recordDuplicateOf (DEDUP-046, DEDUP-063; api.md E32). */
export interface DuplicateOfRequest {
  canonicalRecordId: string | null;
}

export interface StudyClusterMember {
  recordId: string;
  kind: RecordKind;
  title: string;
  year: number | null;
  method: 'registry_id' | 'linked_pmid' | 'human' | 'singleton';
}

export interface StudyCluster {
  /** 'sc_<uuid>'. */
  id: string;
  primaryRegistryId: string | null;
  registryIds: string[];
  /** Set by F16. */
  studyId: string | null;
  members: StudyClusterMember[];
  /** From F15. */
  includedAtLatestStage: boolean;
}

/** GET study-clusters (E111; DEDUP-059). */
export interface StudyClusterListResponse {
  clusters: StudyCluster[];
  nextCursor: string | null;
}

/** PUT records/{recordId}/study-cluster (E112; DEDUP-055). clusterId null moves the record to a new singleton cluster. */
export interface MoveToClusterRequest {
  clusterId: string | null;
  fromClusterId?: string;
}

/** PUT DEDUP_ROUTES.recordDuplicateOf answers with the record and its deduplication detail. */
export type RecordWithDedupDetail = RecordListing & RecordDedupDetail;

/** Added to the RecordListing detail (DEDUP-091). */
export interface RecordDedupDetail {
  duplicateOf: string | null;
  duplicates: string[];
  identifiers: RecordIdentifier[];
  studyClusterIds: string[];
  openCandidateIds: string[];
  warnings: { code: 'invalid_identifier'; scheme: IdentifierScheme; original: string }[];
}

/** F14's part of the PRISMA flow (DEDUP-070 to DEDUP-074). */
export interface PrismaDeduplication {
  duplicatesRemoved: { previouslyIdentified?: number; withinRun?: number; automatic: number; human: number; total: number };
  removedOtherReasons: { notices: number; total: number };
  possibleDuplicatesUnresolved: number;
}
